import React, { useRef, useState } from 'react';
import { Button, Row, Col } from 'antd';

import {
  BOUsersBankingInfoTypeEnum,
  FullBOUserResponseModel,
  ResourceDownloadResponse,
} from 'api/backofficeApiClient';
import { getLocation } from 'utils/locationUtils';
import { getSecureNumberValue } from 'utils/numberUtils';
import EditBankingInfoModal from './EditBankingInfoModal';
import BasePageCard from 'components/wrappers/BasePageCard';
import { getBankingAccountTypeLabel } from 'utils/accountUtils';
import { checkIsCompanyOwner } from 'utils/userRolesUtils';
import { downloadFile, getFileNameWithoutExt } from 'utils/fileUtils';
import BaseSectionMessage from 'components/wrappers/BaseSectionMessage';
import { userSelector } from 'redux/selectors/authenticationSelectors';
import BaseAlert from 'components/wrappers/BaseAlert/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { getBankingInfoFile } from 'redux/reducers/accountSlice';
import useScrollAfterRedirect from 'features/account/hooks/useScrollAfterRedirect';
import { LoadRecruiterDetailsOptions } from 'features/recruiter/RecruiterDetailsPage/types';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';

import styles from './BankingInfo.module.less';

type Props = {
  profile: FullBOUserResponseModel;
  loadFullProfile: (options?: LoadRecruiterDetailsOptions) => Promise<void>;
  bankingInfoFile?: ResourceDownloadResponse;
};

export default function BankingInformation(props: Props) {
  const dispatch = useAppDispatch();
  const { profile, loadFullProfile, bankingInfoFile } = props;
  const { bankingInformation, businessInfo } = profile;
  const user = useAppSelector(userSelector);
  const isCompanyOwner = checkIsCompanyOwner(user);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  useScrollAfterRedirect({ sectionRef });

  const handleOpenModal = () => {
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
  };

  const checkLocation = getLocation(
    bankingInformation?.mailMyChecksDetail?.address,
    bankingInformation?.mailMyChecksDetail?.city,
    bankingInformation?.mailMyChecksDetail?.state,
    bankingInformation?.mailMyChecksDetail?.zip,
  );

  const handleDownloadBankingFile = async () => {
    const result = await dispatch(getBankingInfoFile());

    if (getBankingInfoFile.fulfilled.match(result)) {
      try {
        await downloadFile({
          downloadUrl: result.payload.downloadUrl,
          fileName: result.payload.fileName,
        });
      } catch (e: any) {
        BaseAlert.error(e.message);
      }
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const onEditBankingInfo = async () => {
    await loadFullProfile({ shouldLoadBankingFile: true });
  };

  return (
    <>
      {isModalVisible && (
        <EditBankingInfoModal
          entityInfo={businessInfo?.entityInformation}
          visible={isModalVisible}
          onClose={handleCloseModal}
          onEdit={onEditBankingInfo}
          bankingInfo={bankingInformation}
          bankingFile={bankingInfoFile}
        />
      )}

      <BasePageCard
        ref={sectionRef}
        title="Banking information"
        className="stretch-card"
        icons={<span className="icon icon-banking"></span>}
        subTitle={
          isCompanyOwner &&
          !!bankingInformation && (
            <Button
              onClick={handleOpenModal}
              className="with-icon mobile-icon-only"
              size="large"
              icon={<span className="icon icon-edit" />}
              data-id="openBankingInfoModal"
            >
              Edit
            </Button>
          )
        }
      >
        <div className="content-wrapper">
          {!bankingInformation && isCompanyOwner && (
            <BaseSectionMessage
              type="info"
              title="Info"
              message={
                <div className={styles.message}>
                  <span>Please update your banking information.</span>
                  <Button
                    className={styles.button}
                    onClick={handleOpenModal}
                    type="link"
                  >
                    Fill in now.
                  </Button>
                </div>
              }
            />
          )}

          {!!bankingInformation && (
            <>
              {bankingInformation.bankingInformationType ===
                BOUsersBankingInfoTypeEnum.DirectDeposit && (
                <>
                  <Row gutter={[4, 4]} className="titled-sheet">
                    {!!bankingInformation.directDepositDetail?.bankName && (
                      <Col xs={24} md={12}>
                        <div className="title">Bank name:</div>
                        <div>
                          {bankingInformation.directDepositDetail.bankName}
                        </div>
                      </Col>
                    )}

                    {!!bankingInformation.directDepositDetail?.holderName && (
                      <Col xs={24} md={12}>
                        <div className="title">Bank account holder name:</div>
                        <div>
                          {bankingInformation.directDepositDetail.holderName}
                        </div>
                      </Col>
                    )}

                    {!!bankingInformation.directDepositDetail
                      ?.accountNumber && (
                      <Col xs={24} md={12}>
                        <div className="title">Account number:</div>
                        <div>
                          <span className={styles.breakAll}>
                            {getSecureNumberValue(
                              bankingInformation.directDepositDetail
                                .accountNumber,
                              9,
                            )}
                          </span>
                        </div>
                      </Col>
                    )}

                    {!!bankingInformation.directDepositDetail
                      ?.routingNumber && (
                      <Col xs={24} md={12}>
                        <div className="title">Routing number:</div>
                        <div>
                          <span className={styles.breakAll}>
                            {getSecureNumberValue(
                              bankingInformation.directDepositDetail
                                .routingNumber,
                              5,
                            )}
                          </span>
                        </div>
                      </Col>
                    )}

                    {!!bankingInformation.directDepositDetail?.accountType && (
                      <Col xs={24} md={12}>
                        <div className="title">Account type:</div>
                        <div>
                          {getBankingAccountTypeLabel(
                            bankingInformation.directDepositDetail.accountType,
                          )}
                        </div>
                      </Col>
                    )}

                    {!!bankingInformation.directDepositDetail
                      ?.ddEmailAddress && (
                      <Col xs={24} md={12}>
                        <div className="title">DD email address:</div>
                        <div>
                          {
                            bankingInformation.directDepositDetail
                              .ddEmailAddress
                          }
                        </div>
                      </Col>
                    )}

                    {!!bankingInformation.directDepositDetail
                      ?.isConfirmation && (
                      <Col xs={24}>
                        <div className={styles.note}>
                          <span className="icon icon-check-outlined" />
                          <span>
                            You authorized myBasePay, LLC to initiate
                            credit/debit entries in to the account listed above
                          </span>
                        </div>
                      </Col>
                    )}
                  </Row>
                </>
              )}
              {bankingInformation.bankingInformationType ===
                BOUsersBankingInfoTypeEnum.MailMyChecks && (
                <>
                  <Row gutter={[4, 4]} className="titled-sheet">
                    {!!bankingInformation.mailMyChecksDetail?.payableTo && (
                      <Col xs={24} md={12}>
                        <div className="title">Payable to:</div>
                        <div>
                          {bankingInformation.mailMyChecksDetail.payableTo}
                        </div>
                      </Col>
                    )}

                    {!!checkLocation && (
                      <Col xs={24} md={12}>
                        <div className="title">Check address:</div>
                        <div>{checkLocation}</div>
                      </Col>
                    )}

                    {!!bankingInformation.mailMyChecksDetail
                      ?.isConfirmation && (
                      <Col xs={24}>
                        <div className={styles.note}>
                          <span className="icon icon-check-outlined" />
                          <span>
                            You authorized myBasePay, LLC distribute ‘member
                            commissions’ in the form of a live check
                          </span>
                        </div>
                      </Col>
                    )}
                  </Row>
                </>
              )}
              {bankingInformation.bankingInformationType ===
                BOUsersBankingInfoTypeEnum.International && (
                <>
                  <Row gutter={[4, 4]} className="titled-sheet">
                    {!!bankingInformation.internationalDetail
                      ?.accountHolderName && (
                      <Col xs={24} md={12}>
                        <div className="title">Account holder name:</div>
                        <div>
                          {
                            bankingInformation.internationalDetail
                              .accountHolderName
                          }
                        </div>
                      </Col>
                    )}
                    {!!bankingInformation.internationalDetail
                      ?.internationalBankName && (
                      <Col xs={24} md={12}>
                        <div className="title">Bank name:</div>
                        <div>
                          {
                            bankingInformation.internationalDetail
                              .internationalBankName
                          }
                        </div>
                      </Col>
                    )}
                    {!!bankingInformation.internationalDetail
                      ?.internationalAccountNumber && (
                      <Col xs={24} md={12}>
                        <div className="title">Account number:</div>
                        <div>
                          <span className={styles.breakAll}>
                            {getSecureNumberValue(
                              bankingInformation.internationalDetail
                                .internationalAccountNumber,
                              9,
                            )}
                          </span>
                        </div>
                      </Col>
                    )}
                    {!!bankingInformation.internationalDetail?.swiftCode && (
                      <Col xs={24} md={12}>
                        <div className="title">Swift code:</div>
                        <div>
                          <span className={styles.breakAll}>
                            {getSecureNumberValue(
                              bankingInformation.internationalDetail.swiftCode,
                              7,
                            )}
                          </span>
                        </div>
                      </Col>
                    )}
                    {!!bankingInformation.internationalDetail
                      ?.internationalRoutingCode && (
                      <Col xs={24} md={12}>
                        <div className="title">International routing code:</div>
                        <div>
                          <span className={styles.breakAll}>
                            {getSecureNumberValue(
                              bankingInformation.internationalDetail
                                .internationalRoutingCode,
                              5,
                            )}
                          </span>
                        </div>
                      </Col>
                    )}
                    {!!bankingInformation.internationalDetail?.confirmation1 &&
                      bankingInformation.internationalDetail?.confirmation2 && (
                        <Col xs={24}>
                          <div className={styles.note}>
                            <span className="icon icon-check-outlined" />
                            <span>
                              You authorized myBasePay, LLC to wire currency
                              entries into the account listed above. This
                              account can accept American Currency Transactions
                            </span>
                          </div>
                        </Col>
                      )}
                  </Row>
                </>
              )}
              {bankingInfoFile && (
                <>
                  <div className="title">Download banking information:</div>
                  <span
                    className="link-imitation"
                    onClick={handleDownloadBankingFile}
                  >
                    {getFileNameWithoutExt(bankingInfoFile?.fileName)}
                  </span>
                </>
              )}
            </>
          )}
        </div>
      </BasePageCard>
    </>
  );
}
